// Point-in-country lookup for hover/click. Works in lon/lat space against the per-country rings
// from globe-features.ts. Antimeridian-safe: rings flagged `crosses` are stored unwrapped (negative
// longitudes shifted +360), and the query longitude is unwrapped the same way before testing.

import { COUNTRY_FEATURES, type CountryFeature, type CountryRing } from "@/lib/globe-features";

/** Even-odd ray-casting point-in-polygon for a single ring (coords already in the test frame). */
function pointInRing(lon: number, lat: number, ring: CountryRing): boolean {
  const pts = ring.test;
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i];
    const [xj, yj] = pts[j];
    if (yi > lat !== yj > lat) {
      const xCross = xi + ((lat - yi) / (yj - yi)) * (xj - xi);
      if (lon < xCross) inside = !inside;
    }
  }
  return inside;
}

/**
 * Country containing [lon, lat], or null. Optionally restrict to a set of ISO ids (e.g. only the
 * highlighted/clickable countries) to make click resolution cheaper and unambiguous.
 */
export function countryAt(
  lon: number,
  lat: number,
  restrictTo?: Set<string>
): CountryFeature | null {
  for (const f of COUNTRY_FEATURES) {
    if (restrictTo && !restrictTo.has(f.id)) continue;
    for (const ring of f.rings) {
      const qlon = ring.crosses && lon < 0 ? lon + 360 : lon;
      if (qlon < ring.lonMin || qlon > ring.lonMax || lat < ring.latMin || lat > ring.latMax) {
        continue; // cheap bbox reject
      }
      if (pointInRing(qlon, lat, ring)) return f;
    }
  }
  return null;
}
